/**
 * ConsultationsTable Component - By John Bellew - Infinia.ie - last updated 11/07/2025
 * 
 * A comprehensive table component for displaying consultation data with sorting, search highlighting,
 * and PDF modal functionality. Uses react-table for advanced table features and provides
 * interactive access to consultation documents.
 * 
 * Features:
 * - Sortable columns with visual indicators
 * - Search query highlighting in content
 * - PDF modal integration for document viewing
 * - Category-based color coding
 * - Status indicators (Live, Completed, Pending)
 * - Responsive design with tooltips
 * - No-results state handling
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of consultation objects to display in table
 * @param {number} props.visibleCount - Number of items visible (for pagination context)
 * @param {string} props.searchQuery - Search query string for highlighting matches
 * @returns {JSX.Element} The rendered consultations table component
 * 
 * @example
 * ```jsx
 * <ConsultationsTable 
 *   data={consultationData} 
 *   visibleCount={10} 
 *   searchQuery="regulation" 
 * />
 * ```
 */

import { useMemo, useState } from 'react';
import { useTable, tableFeatures, rowSortingFeature, columnVisibilityFeature, createSortedRowModel, sortFn_alphanumeric, sortFn_text, flexRender } from '@tanstack/react-table';
import PDFModal from '../../modals/PDFModal';
import { formatDate, formatDateSimple, normalizeText, truncateText } from '../../utils/textUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortUp, faSortDown, faSort } from '@fortawesome/free-solid-svg-icons';
import './ConsultationsTable.css';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
// Declared once at module scope so the table is not rebuilt on every render.
// v9 no longer bundles the built-in sort functions: auto-detected columns look
// their sort function up by name and silently fall back to a case-sensitive
// basic compare if it is not registered here.
const features = tableFeatures({
    rowSortingFeature,
    columnVisibilityFeature,
    sortedRowModel: createSortedRowModel(),
    sortFns: {
        alphanumeric: sortFn_alphanumeric,
        text: sortFn_text,
    },
});

const ConsultationsTable = ({ data, visibleCount, searchQuery }) => {
    const [modals, setModals] = useState([]);

    /**
     * Get CSS color class for consultation category
     * 
     * Maps consultation categories to predefined color classes for consistent styling
     * across the table. Performs case-insensitive matching and supports partial matches.
     * 
     * @param {string} category - The consultation category
     * @returns {string} CSS class name for the category color
     * 
     * @example
     * ```javascript
     * getCategoryColor('Consultation') // Returns 'consultation'
     * getCategoryColor('Technical') // Returns 'teal'
     * getCategoryColor('unknown') // Returns 'light-gray'
     * ```
     */
    const getCategoryColor = (category) => {
        const colorMap = {
            'Consultation': 'consultation',
            'Paper': 'paper',
            'Submission': 'submission',
            'Response': 'coral',
            'Statement': 'dust-rose',
            'Guidance': 'cyan',
            'Policy': 'peach',
            'Regulation': 'sage',
            'Directive': 'purple',
            'Technical': 'teal',
            'Legal': 'indigo'
        };
        
        const normalizedCategory = category?.toLowerCase();
        const matchedKey = Object.keys(colorMap).find(key => 
            key.toLowerCase() === normalizedCategory || 
            normalizedCategory?.includes(key.toLowerCase())
        );
        
        return colorMap[matchedKey] || 'light-gray';
    };

    /**
     * Open a PDF modal or bring existing modal to front
     * 
     * Manages multiple PDF modals with proper z-index stacking. If a modal for the same
     * consultation already exists, it brings it to the front. Otherwise, creates a new modal
     * with proper positioning and z-index management.
     * 
     * @param {string|number} id - Unique identifier for the consultation
     * @param {string} title - Title to display in the modal header
     * @param {string} pdfUrl - URL of the PDF document to display
     * 
     * @example
     * ```javascript
     * openPDFModal('consultation-123', 'Consultation Title', 'https://example.com/doc.pdf')
     * ```
     */
    const openPDFModal = (id, title, pdfUrl, consultationData = null) => {
        setModals(prevModals => {
            const existingModalIndex = prevModals.findIndex(modal => modal.id === id);
            const maxZIndex = Math.max(...prevModals.map(modal => modal.zIndex), 0) + 1;

            if (existingModalIndex !== -1) {
                const updatedModals = [...prevModals];
                updatedModals[existingModalIndex].isOpen = true;
                updatedModals[existingModalIndex].zIndex = maxZIndex;
                return updatedModals;
            } else {
                const newModal = {
                    id,
                    title,
                    pdfUrl,
                    consultationData,
                    isOpen: true,
                    top: `${15 + (prevModals.length * 2)}%`,
                    left: `${30 + (prevModals.length * 2)}%`,
                    zIndex: maxZIndex,
                };
                return [...prevModals, newModal];
            }
        });
    };

    /**
     * Close a PDF modal by ID
     * 
     * Marks the specified modal as closed while maintaining it in the modals array
     * for potential reopening. The modal component handles the actual visibility.
     * 
     * @param {string|number} id - Unique identifier of the modal to close
     * 
     * @example
     * ```javascript
     * closePDFModal('consultation-123')
     * ```
     */
    const closePDFModal = id => {
        setModals(prevModals => prevModals.map(modal => modal.id === id ? { ...modal, isOpen: false } : modal));
    };

    /**
     * Table column definitions using react-table format
     * 
     * Defines the structure and behavior of each table column including:
     * - Header text and sorting behavior
     * - Cell rendering with custom formatting
     * - Search highlighting integration
     * - PDF modal integration for clickable elements
     * - Category and status tag rendering
     * 
     * @type {Array} Column configuration array for react-table
     */
    const columns = useMemo(() => [
        {
            header: 'Name & Topic',
            accessorKey: 'name',
            id: 'name',
            cell: ({ row }) => (
                <div className="table-content">
                    <h4 className="table-title">
                        {row.original.link ? (
                            <a className='consultation-link' onClick={e => {
                                e.stopPropagation();
                                openPDFModal(row.original.id, row.original.name, row.original.longFileName || row.original.link, row.original); 
                            }}>
                                {searchQuery.trim() ? (
                                    <span dangerouslySetInnerHTML={{ 
                                        __html: normalizeText(row.original.name).replace(
                                            new RegExp(searchQuery.trim(), 'gi'), 
                                            match => `<span class="highlight-match">${match}</span>`
                                        )
                                    }} />
                                ) : (
                                    normalizeText(row.original.name)
                                )}
                            </a>
                        ) : (
                            searchQuery.trim() ? (
                                <span dangerouslySetInnerHTML={{ 
                                    __html: normalizeText(row.original.name).replace(
                                        new RegExp(searchQuery.trim(), 'gi'), 
                                        match => `<span class="highlight-match">${match}</span>`
                                    )
                                }} />
                            ) : (
                                normalizeText(row.original.name)
                            )
                        )}
                    </h4>
                    {row.original.topic && row.original.topic !== 'N/A' && (
                        <Tippy content={row.original.topic}>
                            <p className="table-summary">
                                {searchQuery.trim() ? (
                                    <span dangerouslySetInnerHTML={{ 
                                        __html: truncateText(normalizeText(row.original.topic), 150).replace(
                                            new RegExp(searchQuery.trim(), 'gi'), 
                                            match => `<span class="highlight-match">${match}</span>`
                                        )
                                    }} />
                                ) : (
                                    truncateText(normalizeText(row.original.topic), 150)
                                )}
                            </p>
                        </Tippy>
                    )}
                    <div className="table-tags">
                        {row.original.category && (
                            <span className={`tag category-tag tag-${getCategoryColor(row.original.category)}`}>
                                {row.original.category}
                            </span>
                        )}
                        {row.original.status && (
                            <span className={`tag status-tag ${row.original.status === 'Live' ? 'tag-live' : row.original.status === 'Completed' ? 'tag-completed' : 'tag-pending'}`}>
                                {row.original.status}
                            </span>
                        )}
                    </div>
                </div>
            ),
        },
        {
            header: 'Working Group & Issuing Body',
            accessorKey: 'wg',
            id: 'wg',
            cell: ({ row }) => (
                <div className="table-meta">
                    {row.original.wg && row.original.wg !== 'N/A' && (
                        <div className="meta-item">
                            <i className="fas fa-users"></i>
                            <Tippy content={row.original.wg}>
                                <span className="meta-text">
                                    {searchQuery.trim() ? (
                                        <span dangerouslySetInnerHTML={{ 
                                            __html: truncateText(row.original.wg, 30).replace(
                                                new RegExp(searchQuery.trim(), 'gi'), 
                                                match => `<span class="highlight-match">${match}</span>`
                                            )
                                        }} />
                                    ) : (
                                        truncateText(row.original.wg, 30)
                                    )}
                                </span>
                            </Tippy>
                        </div>
                    )}
                    {row.original.ib && row.original.ib !== 'N/A' && (
                        <div className="meta-item">
                            <i className="fas fa-building"></i>
                            <Tippy content={row.original.ib}>
                                <span className="meta-text">
                                    {searchQuery.trim() ? (
                                        <span dangerouslySetInnerHTML={{ 
                                            __html: truncateText(row.original.ib, 30).replace(
                                                new RegExp(searchQuery.trim(), 'gi'), 
                                                match => `<span class="highlight-match">${match}</span>`
                                            )
                                        }} />
                                    ) : (
                                        truncateText(row.original.ib, 30)
                                    )}
                                </span>
                            </Tippy>
                        </div>
                    )}
                </div>
            ),
        },
        {
            header: 'Completed Date',
            accessorFn: row => {
                // If it's Live, always sort it last
                if (row.status === 'Live') return Infinity;
                if (!row.completed || row.completed === 'No Date Available') return Infinity;
                return new Date(row.completed).getTime();
            },
            id: 'completed',
            cell: ({ row }) => (
                <div className="table-date">
                    <i className="fas fa-calendar-alt"></i>
                    <span>
                        {row.original.status === 'Completed' ? (
                            <Tippy content={`Completed: ${formatDateSimple(row.original.completed)}`}>
                                <span>{formatDate(row.original.completed)}</span>
                            </Tippy>
                        ) : (
                            <span className={`status-display ${row.original.status?.toLowerCase()}`}>
                                {row.original.status}
                            </span>
                        )}
                    </span>
                </div>
            ),
        },
        {
            header: 'Actions',
            accessorKey: 'actions',
            enableSorting: false,
            id: 'actions',
            cell: ({ row }) => (
                <div className="table-actions">
                    {row.original.link ? (
                        <button 
                            className="read-more-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                openPDFModal(row.original.id, row.original.name, row.original.longFileName || row.original.link, row.original);
                            }}
                        >
                            <span>View</span>
                            <i className="fas fa-external-link-alt"></i>
                        </button>
                    ) : (
                        <span className="no-link">No Document</span>
                    )}
                </div>
            )
        }
    ], [searchQuery]);

    /**
     * React Table hooks for managing table behavior
     * 
     * Configures the table with sorting capabilities and data binding.
     * The disableSortRemove option prevents clearing sort state when clicking headers.
     */
    const table = useTable({
        features,
        data,
        columns,
        enableSortingRemoval: false,
    });

    return (
        <div className="consultations-table">
            {data.length === 0 ? (
                <div className="no-results">
                    <div className="no-results-content">
                        <i className="fas fa-search"></i>
                        <h3>No consultations found</h3>
                        <p>Try adjusting your search criteria or filters to see more results.</p>
                    </div>
                </div>
            ) : (
                <div className="table-container">
                   <table className="consultation-table">
                    <thead>
                        {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map(header => {
                            const column = header.column;
                            const isSorted = column.getIsSorted(); // 'asc' | 'desc' | false
                            const canSort = column.getCanSort();

                            return (
                                <th
                                key={header.id}
                                colSpan={header.colSpan}
                                className={`col-${column.id} ${isSorted ? 'sorted' : ''}`}
                                data-sortable={canSort}
                                data-sorted={!!isSorted}
                                aria-sort={
                                    isSorted === 'desc'
                                    ? 'descending'
                                    : isSorted === 'asc'
                                    ? 'ascending'
                                    : 'none'
                                }
                                onClick={canSort ? column.getToggleSortingHandler() : undefined}
                                style={{ cursor: canSort ? 'pointer' : 'default' }}
                                >
                                <div className="header-content">
                                    <span className="header-text">
                                    {flexRender(column.columnDef.header, header.getContext())}
                                    </span>
                                    {canSort ? (
                                    <span className={`sort-icon ${isSorted ? 'active' : ''}`}>
                                        {isSorted === 'desc' ? (
                                        <FontAwesomeIcon icon={faSortDown} title="Sorted Descending" />
                                        ) : isSorted === 'asc' ? (
                                        <FontAwesomeIcon icon={faSortUp} title="Sorted Ascending" />
                                        ) : (
                                        <FontAwesomeIcon icon={faSort} title="Click to Sort" />
                                        )}
                                    </span>
                                    ) : (
                                    <span className="sort-icon disabled" style={{ opacity: 0.3 }}>
                                        <i className="fas fa-grip-vertical" title="Not Sortable" />
                                    </span>
                                    )}
                                </div>
                                </th>
                            );
                            })}
                        </tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map(row => (
                        <tr key={row.id} className="table-row">
                            {row.getVisibleCells().map(cell => (
                            <td key={cell.id}>
                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                            </td>
                            ))}
                        </tr>
                        ))}
                    </tbody>
                    </table>
                </div>
            )}

            {modals.map((modal, index) => (
                <PDFModal
                    key={`${modal.id}-${index}`}
                    title={modal.title}
                    url={modal.pdfUrl}
                    show={modal.isOpen} 
                    onClose={e => {
                        e.stopPropagation(); 
                        closePDFModal(modal.id);
                    }}
                    allowFullscreen={true}
                    allowMove={true}
                    showLinkinToolBar={true}
                    style={{ zIndex: modal.zIndex }}
                    consultationData={modal.consultationData}
                />
            ))}
        </div>
    );
};

export default ConsultationsTable;
